// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

// TODO: CLEAN UP, horrible implementation, do we even need the ifame and or this component!??

import { AsyncPipe, NgIf } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PatternMatchComponent } from 'src/app/modules/patternmatch/components/pattern-match/pattern-match.component';
import { PatternMatchDataService } from 'src/app/modules/patternmatch/services/pattern-match-data/pattern-match-data.service';
import { ProcessPlaceholdersPipe } from 'src/app/modules/shared/pipes/process-placeholders.pipe';
import { CoilsComponent } from '../../../coils/components/coils/coils.component';
import { APICallGateComponent } from '../../../shared/components/api-call-gate.component';
import { SanitizeResourceUrlPipe } from '../../../shared/pipes/sanitizeResourceUrl.pipe';
import { TubeComponent } from '../../../tube/components/tube/tube.component';
import { RouteService } from '../../services/routeService.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import { BackToSystemOverviewComponent } from '../backToSystemOverview/backToSystemOverview.component';
import { CoolingSystemDetailsComponent } from 'src/app/modules/cooling-companion/components/cooling-system-details/cooling-system-details.component';
import { CoolingSystemComponentService } from '../../services/cooling-system-component.service';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights-service/application-insights.service';

export const Tabs = {
  CoolingSystem: 'cooling-system',
} as const;

@Component({
  selector: 'app-embedded-view',
  templateUrl: './embeddedView.component.html',
  styleUrls: ['./embeddedView.component.css'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgIf,
    BackToSystemOverviewComponent,
    APICallGateComponent,
    AsyncPipe,
    CoolingSystemDetailsComponent,
    PatternMatchComponent,
    TubeComponent,
    CoilsComponent,
    SanitizeResourceUrlPipe,
    ProcessPlaceholdersPipe,
  ],
  providers: [SanitizeResourceUrlPipe, ProcessPlaceholdersPipe],
})
export class EmbeddedViewComponent {
  componentId: any;
  systemDetails: any;
  router = inject(Router);
  route = inject(ActivatedRoute);
  routeService = inject(RouteService);
  applicationInsights = inject(ApplicationInsightsService);
  coolingSystemComponentService = inject(CoolingSystemComponentService);
  sanitizeResourceUrlPipe = inject(SanitizeResourceUrlPipe);
  systemOverviewService = inject(SystemOverviewService);
  patternMatchDataService = inject(PatternMatchDataService);
  tabOrder = 1;

  systemDetails$ = this.systemOverviewService.systemDetails.lastSuccessfulData$;

  dataSourceGroupId$ = this.patternMatchDataService.component$.pipe(
    map((component) => {
      return component?.dataSourceGroupId;
    }),
  );

  dataSourceGroup$ = combineLatest({
    entireOverview:
      this.systemOverviewService.entireOverview.lastSuccessfulData$,
    dataSourceGroupId: this.dataSourceGroupId$,
  }).pipe(
    map(({ dataSourceGroupId, entireOverview }) =>
      entireOverview.dataSourceGroups.find((c) => c.id === dataSourceGroupId),
    ),
  );

  externalWebApp$ = this.routeService.routeData$.pipe(
    map((data) => data?.externalWebApp as string | null),
  );

  constructor() {
    // hydrate system info
    // we need system overview AND modilities list
    // so we can resolve modalityIds to modalityDisplayNames
    this.systemOverviewService.systemDetails.lastSuccessfulData$
      .pipe(takeUntilDestroyed())
      .subscribe((systemDetails) => {
        this.systemDetails = systemDetails;
        this.systemOverviewService.entireOverview.request({
          systemMaterialNumber: systemDetails.systemMaterialNumber,
          systemSerialNumber: systemDetails.systemSerialNumber,
          modalityId: systemDetails.modalityId,
        });
      });

    // To capture tab changes from query params using deep linking
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      if (params['tab'] === Tabs.CoolingSystem) {
        this.tabOrder = 2;
        this.applicationInsights.trackUserEvent('CoolingCompanionPage');
      }
    });

    this.getComponentId();
    this.trackNameAndSystemDetailsForAppInsights();
  }

  private trackNameAndSystemDetailsForAppInsights(): void {
    this.trackComponentsWithNoPatternMatchAnalysisData();
    this.trackComponentsWithPatternMatchAnalysisData();
  }

  private trackComponentsWithNoPatternMatchAnalysisData(): void {
    this.patternMatchDataService.component$
      .pipe(
        filter((component) => {
          const name = component?.displayName.toLocaleLowerCase();
          return name === 'local coils' || name === 'x-ray tubes';
        }),
        takeUntilDestroyed(),
      )
      .subscribe((component) => {
        if (!component) {
          return;
        }
        this.applicationInsights.trackComponentUsageEvent('Component Usage', {
          ComponentName: component.displayName.toLocaleLowerCase(),
        });
      });
  }

  private trackComponentsWithPatternMatchAnalysisData(): void {
    combineLatest({
      component: this.patternMatchDataService.component$,
      pmAnalysis: this.patternMatchDataService.pmAnalysis.lastSuccessfulData$,
    })
      .pipe(
        // Guard against the race condition where component$ changes (immediately on navigation)
        // before pmAnalysis.request() has been called with the new component's params.
        // lastSuccessfulData$ is a ReplaySubject(1) so it replays stale data until the new
        // request completes. We skip those stale combinations by checking lastParams.
        filter(({ component }) => {
          const lastParams = this.patternMatchDataService.pmAnalysis.lastParams;
          if (!component || !lastParams) return false;
          const componentName =
            component.filterName ?? component.displayName ?? 'n.a.';
          return lastParams.componentName === componentName;
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ component, pmAnalysis }) => {
        if (!component) {
          return;
        }
        const componentName = component.displayName.toLocaleLowerCase();
        const patternDetails = pmAnalysis.patternMatches?.map((pm) => ({
          PatternName: pm.pattern,
          PatternDescription: pm.patternDescription,
          Dates: pm.matchFindings.map((f) => f.date),
        }));

        this.applicationInsights.trackComponentUsageEvent(
          'ComponentUsageWithPatternMatch',
          {
            ComponentName: componentName,
            PatternMatchExists:
              pmAnalysis.patternMatches?.length > 0 ? 'Yes' : 'No',
            PatternDetails: JSON.stringify(patternDetails),
          },
        );
      });
  }

  private getComponentId() {
    this.routeService.currentComponent$
      .pipe(takeUntilDestroyed())
      .subscribe((componentId) => {
        if (!componentId) {
          return;
        }
        this.componentId = componentId;
      });
  }

  checkIfCoolingSystemComponent(): boolean {
    return this.coolingSystemComponentService.checkIfCoolingSystemComponentIsOfCtNaeotomAlpha(
      this.componentId,
      this.systemDetails,
    );
  }

  activateTabAndUpdateQueryParams(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    if (this.tabOrder === 2) {
      queryParams['tab'] = Tabs.CoolingSystem;
    } else {
      delete queryParams['tab'];
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }
}
